import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, getDoc, addDoc, updateDoc, deleteDoc, doc, Timestamp, deleteField } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { ChevronLeft, RefreshCw, Trash2, FileText, RotateCcw } from 'lucide-react';
import { displayPatientName } from '../utils/patientName';
import { partnerKeyOf } from '../utils/billingCycle';
import { loadLedgerSources } from '../utils/receivables';   // 예전 기록(statementId 없음) 되돌릴 때 명세서 줄 확인 — 세션 캐시 공용 로더 (P1-3·P1-4)

// 에이전트 수집함 — 거래처 무관, 모든 pending parsedCases를 치과별로 확인
// 치과+기간을 골라 [명세서 만들기]를 누르면 의뢰서 등록 후 작성 화면으로 그대로 넘긴다.
function ParsedCasesInbox({ user, onBack, onCreateStatement }) {
    const navigate = useNavigate();
    const labId = user?.companyId || auth.currentUser?.uid;
    const labName = user?.businessName || user?.companyName || user?.name || '기공소';
    // 직원 계정 — rules 상 수집 건을 새로 '제외'로 바꾸는 건 기공소 대표만 (2026-10-23 계약 §3). 직원에게는 [제외] 버튼을 숨긴다
    // 대표 본인 계정도 companyId == 자기 uid 일 수 있다(CompanyManager.js) → companyId 가 남의 uid 일 때만 직원
    const isStaff = user?.userType === 'staff' || (!!user?.companyId && user.companyId !== auth.currentUser?.uid);

    const [rows, setRows] = useState([]);
    const [checked, setChecked] = useState({});
    const [loading, setLoading] = useState(true);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [clinicFilter, setClinicFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');   // 기본 전체 — 명세화된 건도 목록에 항상 표시 (2026-07-26 사장 확정)
    const [busy, setBusy] = useState(false);
    const [agentStatus, setAgentStatus] = useState(undefined);     // undefined=로딩중, null=문서 없음
    const [stmtStates, setStmtStates] = useState({});   // '명세서에 담김' 3구분용 statementId → 'sent' | 'draft' | 'gone' | 'error' | 'loading'

    // 에이전트 하트비트(agentStatus/{labId}) — 연결 상태 배지용 (1회 조회)
    useEffect(() => {
        if (!labId) return;
        let alive = true;
        getDoc(doc(db, 'agentStatus', labId))
            .then(snap => { if (alive) setAgentStatus(snap.exists() ? snap.data() : null); })
            .catch(() => { if (alive) setAgentStatus(null); });
        return () => { alive = false; };
    }, [labId]);

    const load = async () => {
        if (!labId) return;
        setLoading(true);
        try {
            // labId 단일 equality로만 조회(복합 인덱스 불필요) → 상태는 클라이언트에서 필터
            const snap = await getDocs(query(
                collection(db, 'parsedCases'),
                where('labId', '==', labId),
            ));
            let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            list = list.filter(d => d.sourceType !== 'scan_only'); // 스캔만 있는 폴더(치식 없음, 명세서용 아님) 제외 (2026-08-05)
            // 상태 필터는 클라이언트(visible)에서만 — 탭 전환마다 재조회하지 않는다 (2026-08-26 성능)
            list.sort((a, b) => (b.fileMtime || '').localeCompare(a.fileMtime || ''));
            setRows(list);
            setChecked({});
            setStmtStates({});   // 새로고침 = 명세서 상태도 다시 확인
        } catch (e) {
            console.error('수집 목록 로드 실패:', e);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [labId]);

    // 명세서화된 건을 수집함으로 되돌리기 — 연결 의뢰서 삭제 + 상태 pending 복귀
    //   (2026-10-24 재점검 P1-3) 예전 기록(imported + orderId, statementId 없음)은 orderId 로 명세서 줄이 있는지 먼저 본다:
    //     보낸 명세서에 있으면 되돌리지 않고 그 명세서로 연결만 기록 / 작성 중 명세서에 있으면 연결 기록 후 그 명세서에서 줄을 지우라고 안내 /
    //     어디에도 없으면 수집 대기로 되돌린다. 확인을 못 하면 진행하지 않는다.
    const revertToInbox = async (r) => {
        const who = displayPatientName(r).name || '이 건';
        if (!r.statementId && r.orderId) {
            let statements;
            try {
                statements = (await loadLedgerSources(labId)).statements;
            } catch (e) {
                console.error('[수집함] 되돌리기 전 명세서 확인 실패:', r.id, e?.code || e?.message);
                alert('명세서를 확인하지 못해 되돌리지 않았습니다. 새로고침 뒤 다시 시도해 주세요.');
                return;
            }
            const holder = statements.find(s => (Array.isArray(s.items) ? s.items : []).some(it => it && (it.orderId === r.orderId || it.parsedCaseId === r.id)));
            if (holder && ['confirmed', 'published'].includes(holder.status) && holder.deletedBySender !== true) {
                try {
                    await updateDoc(doc(db, 'parsedCases', r.id), { statementId: holder.id });
                    setRows(prev => prev.map(x => x.id === r.id ? { ...x, statementId: holder.id } : x));
                } catch (e) { console.warn('[수집함] 명세서 연결 기록 실패:', r.id, e?.code || e?.message); }
                alert(`${who}은 이미 보낸 명세서(${holder.statementNumber || '번호 없음'})에 담겨 있어 되돌리지 않았습니다.\n빼려면 그 명세서를 고쳐서 다시 발행해 주세요.`);
                return;
            }
            if (holder && holder.status === 'draft') {
                try {
                    await updateDoc(doc(db, 'parsedCases', r.id), { statementId: holder.id });
                    setRows(prev => prev.map(x => x.id === r.id ? { ...x, statementId: holder.id } : x));
                } catch (e) { console.warn('[수집함] 명세서 연결 기록 실패:', r.id, e?.code || e?.message); }
                alert(`${who}은 작성 중 명세서(${holder.statementNumber || '번호 없음'})에 담겨 있습니다.\n빼려면 그 명세서에서 줄을 지우고 저장하세요 — 그러면 자동으로 수집 대기로 돌아옵니다.`);
                return;
            }
        }
        if (!window.confirm(`${who}을 수집함으로 되돌릴까요?\n연결된 의뢰서가 삭제됩니다.\n(이미 명세서에 담긴 행은 그대로 남습니다 — 필요하면 명세서 화면에서 직접 삭제하세요)`)) return;
        try {
            if (r.orderId) { try { await deleteDoc(doc(db, 'workOrders', r.orderId)); } catch (e) { console.warn('의뢰서 삭제 실패(이미 없음 가능):', e); } }
            // statementId 도 지운다 — 남겨 두면 배지·저장 때 되돌림(P1-2)이 옛 명세서를 계속 가리킨다 (2026-10-24 재점검)
            await updateDoc(doc(db, 'parsedCases', r.id), { status: 'pending', orderId: null, importedAt: null, statementId: deleteField() });
            setRows(prev => prev.map(x => x.id === r.id ? { ...x, status: 'pending', orderId: null, statementId: undefined } : x)); // 목록에서 제거하지 않고 상태만 갱신 — 되돌린 행 유지, [명세서 반영됨] 탭에서만 빠짐 (2026-08-26)
        } catch (e) {
            console.error('되돌리기 실패:', e);
            alert('되돌리기에 실패했습니다.');
        }
    };

    // 치과명 표시: 매칭된 거래처명 우선, 없으면 파일 기재 치과명
    const clinicOf = (r) => (r.partnerName || r.clientNameInFile || '미확인').trim();

    const clinicOptions = useMemo(() => {
        const set = new Set(rows.map(clinicOf));
        return ['all', ...Array.from(set).sort()];
    }, [rows]);

    const visible = useMemo(() => rows.filter(r => {
        // 상태 필터 (클라이언트 전환 — 2026-08-26): 제외 건은 [제외됨] 탭에서만 표시
        const st = r.status || 'pending';
        if (statusFilter === 'excluded') {
            if (st !== 'excluded') return false;
        } else {
            if (st === 'excluded') return false;
            if (statusFilter !== 'all' && st !== statusFilter) return false;
        }
        const d = (r.fileMtime || '').slice(0, 10);
        if (from && d < from) return false;
        if (to && d > to) return false;
        if (clinicFilter !== 'all' && clinicOf(r) !== clinicFilter) return false;
        return true;
    }), [rows, statusFilter, from, to, clinicFilter]);

    // 치과별 접기/펴기 (2026-07-26 사장 요청 — 기본 접힘, 치과 행 클릭으로 토글)
    const [openClinics, setOpenClinics] = useState({});
    const toggleClinic = (c) => setOpenClinics(prev => ({ ...prev, [c]: !prev[c] }));

    // ── '명세서에 담김' 3구분 (명세 §5-4, 2026-10-23) ──
    // imported 케이스의 statementId 로 명세서 상태를 본다: 보냄 / 초안에만 / 명세서가 지워짐.
    // 읽기 최소화: 펼친 치과의 imported 줄에 필요한 명세서만, 같은 id 는 한 번만, 10개씩 묶어 조회.
    useEffect(() => {
        const need = new Set();
        visible.forEach(r => {
            if ((r.status || 'pending') !== 'imported' || !r.statementId) return;
            if (!openClinics[clinicOf(r)]) return;
            if (stmtStates[r.statementId] === undefined) need.add(r.statementId);
        });
        if (!need.size) return;
        const ids = [...need];
        setStmtStates(prev => { const n = { ...prev }; ids.forEach(id => { n[id] = 'loading'; }); return n; });
        (async () => {
            const result = {};
            let failed = 0;
            const fetched = [];
            for (let i = 0; i < ids.length; i += 10) {
                const chunk = ids.slice(i, i + 10);
                // eslint-disable-next-line no-await-in-loop
                fetched.push(...await Promise.all(chunk.map(id => getDoc(doc(db, 'transactionStatements', id))
                    .then(snap => ({ id, snap }))
                    .catch(e => ({ id, err: e })))));
            }
            fetched.forEach(({ id, snap, err }) => {
                    if (err) {
                        // 지워진 문서는 rules 가 resource 없음으로 거부(permission-denied)하므로 '지워짐'으로 본다
                        if (err.code === 'permission-denied' || err.code === 'not-found') { result[id] = 'gone'; }
                        else { result[id] = 'error'; failed += 1; }
                        return;
                    }
                    if (!snap.exists()) { result[id] = 'gone'; return; }
                    const st = snap.data();
                    if (st.deletedBySender === true) {
                        // 정정된 원본이면 정정본에 이미 담겨 있다(계약 §3) — 지워진 것은 정정 없이 삭제표시된 발행본만
                        result[id] = st.correctedTo ? 'sent' : 'gone';
                    } else if (st.status === 'draft') {
                        result[id] = 'draft';
                    } else {
                        result[id] = 'sent';
                    }
            });
            if (failed) console.warn('[수집함] 명세서 상태 조회 실패', failed, '건');   // 건수만 (환자명 미출력)
            // 다른 치과를 펼쳐 효과가 다시 돌아도 결과는 버리지 않는다('확인 중'에 멈추지 않게)
            setStmtStates(prev => ({ ...prev, ...result }));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, openClinics]);

    // 줄 배지: imported 케이스가 지금 어떤 명세서에 담겨 있는지
    const inStatementBadge = (r) => {
        const st = r.statementId ? stmtStates[r.statementId] : 'none';
        if (st === 'sent') return { t: '보냄', bg: '#EDFBF1', c: '#15803D' };
        if (st === 'draft') return { t: '작성 중 명세서에만', bg: '#FFFBEB', c: '#B45309' };
        if (st === 'gone') return { t: '명세서가 지워짐 → 다시 담아야 함', bg: '#FEF2F2', c: '#DC2626' };
        if (st === 'error') return { t: '확인 못 함 — 새로고침해 주세요', bg: '#F1F5F9', c: '#64748B' };
        if (st === 'none') return { t: '명세서에 담김 (예전 기록 — 보냄 여부 확인 불가)', bg: '#F1F5F9', c: '#64748B' };
        return { t: '확인 중…', bg: '#F1F5F9', c: '#94A3B8' };
    };

    // [이 치과 초안 만들기] — 거래명세서 목록의 마감 초안 만들기로 보낸다 (명세 §3-3·§5-4).
    // 받는 쪽(B, TransactionStatementList)과 맞춘 전달 형식: location.state.closingDraftFor = { partnerKey, partnerName }
    const goClosingDraft = (clinicName, items) => {
        const partnerId = items.find(r => r.partnerId)?.partnerId;
        const partnerKey = partnerKeyOf(partnerId || `agent_${clinicName}`, clinicName);   // 거래처 키 정본(회원 uid / 비회원 'name:'+정규화 이름)
        if (onBack) onBack();
        navigate('/transactions', { state: { closingDraftFor: { partnerKey, partnerName: clinicName } } });
    };

    // 치과별 그룹 (등록 시 치과 단위로 묶기 위함)
    const grouped = useMemo(() => {
        const m = new Map();
        visible.forEach(r => {
            const k = clinicOf(r);
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(r);
        });
        return Array.from(m.entries());
    }, [visible]);


    // 대상 건들을 의뢰서로 등록한 뒤, 그 치과 + 수집함에서 고른 기간을 명세서 작성 화면으로 넘김
    const createStatementFrom = async (targetRows) => {
        if (!targetRows.length) { alert('명세서로 만들 항목을 선택해주세요.'); return; }
        // 명세서는 치과 1곳 단위 — 여러 치과가 섞이면 막음
        const clinicSet = [...new Set(targetRows.map(clinicOf))];
        if (clinicSet.length > 1) {
            alert('명세서는 [거래명세서] 메뉴에서 치과와 기간을 골라 만듭니다.');
            return;
        }
        const clinicName = clinicSet[0];
        if (!window.confirm(`${clinicName} · ${targetRows.length}건으로 명세서를 만들까요?\n의뢰서로 등록하고 명세서 작성 화면으로 이동합니다.`)) return;
        setBusy(true);
        let ok = 0;
        const dates = [];
        const doneIds = new Set();   // 등록 성공 건
        for (const r of targetRows) {
            try {
                const fileDate = r.fileMtime ? new Date(r.fileMtime) : new Date();
                // 로컬(KST) 날짜 기준 — toISOString은 UTC라 오전 9시 이전 작업이 전날 번호로 밀림 (2026-08-26)
                const dateStr = `${fileDate.getFullYear()}${String(fileDate.getMonth() + 1).padStart(2, '0')}${String(fileDate.getDate()).padStart(2, '0')}`;
                const orderNumber = `AGT-${dateStr}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;
                const indication = (r.indications || [])[0] || '';
                const realPartnerId = r.partnerId || null;   // 미연결 치과는 id 없음
                const orderRef = await addDoc(collection(db, 'workOrders'), {
                    orderNumber,
                    fromUserId: realPartnerId,
                    fromUserName: clinicName,
                    toUserId: labId,
                    toUserName: labName,
                    participants: [realPartnerId, labId].filter(Boolean),
                    patientName: displayPatientName(r).name || '환자미상',
                    patientNameRaw: r.patientNameRaw || r.patientName || '',
                    items: (r.teethDetails && r.teethDetails.length)
                        ? r.teethDetails.map(d => ({
                            toothNumber: d.tooth, prosthesis: d.prosthesis || indication, material: d.material || r.material || '',
                            shade: d.shade || '', implant: d.implant || '', ...(d.implantSystem ? { implantSystem: d.implantSystem } : {}), isPontic: !!d.isPontic, isBridge: !!d.isBridge,
                        }))
                        : (r.teeth || []).map(t => ({
                            toothNumber: t, prosthesis: indication, material: r.material || '',
                            shade: '', implant: '', isPontic: false, isBridge: false,
                        })),
                    prosthesisGroups: [], bridgeGroups: [], ponticGroups: [], implantGroups: [],
                    dueDate: Timestamp.fromDate(fileDate),
                    specialNotes: `에이전트 자동 수집 (${r.sourceType} · ${r.sourceFile || ''})${r.isRemake ? ' · 리메이크' : ''}`,
                    imageUrls: [],
                    isRemake: !!r.isRemake, remakeType: '', remakeReason: r.isRemake ? '에이전트 감지 (재제작 파일)' : '',
                    autoCollected: true, source: 'agent_auto', parsedCaseId: r.id,
                    readByReceiver: true,
                    createdAt: Timestamp.fromDate(fileDate), updatedAt: new Date(),
                });
                // (2026-10-24 재점검 P1-3) status 는 pending 그대로 — orderId 만 적는다. 'imported' 전환은 작성 화면 저장(markCasesInStatement)에서만.
                //   저장 없이 떠나도 수집 대기로 남아 다시 보인다.
                await updateDoc(doc(db, 'parsedCases', r.id), { orderId: orderRef.id, importedAt: new Date() });
                if (r.fileMtime) dates.push(r.fileMtime.slice(0, 10));
                doneIds.add(r.id);
                ok++;
            } catch (e) { console.error('의뢰서 등록 실패:', r.id, e?.code || e?.message); }   // id 만(환자명 미출력)
        }
        setBusy(false);
        setRows(prev => prev.filter(r => !doneIds.has(r.id)));   // 성공한 건만 목록에서 뺀다 — 실패 건은 남아 다시 시도 가능
        setChecked({});
        if (ok === 0) { alert('등록에 실패했습니다. 다시 시도해주세요.'); return; }
        // 일부 실패는 건수로 알린다 (2026-10-24 재점검 P2-14 — 조용한 부분 실패 금지)
        if (ok < targetRows.length) alert(`${targetRows.length}건 중 ${ok}건만 등록했습니다. 실패한 ${targetRows.length - ok}건은 수집함에 그대로 남아 있습니다 — 다시 시도해 주세요.`);
        // 넘길 기간: 수집함 필터가 있으면 그대로, 없으면 이 건들의 작업일 범위
        dates.sort();
        const partnerId = targetRows.find(r => r.partnerId)?.partnerId;
        const clinicId = partnerId || `agent_${clinicName}`;
        const outFrom = from || dates[0] || '';
        const outTo = to || dates[dates.length - 1] || '';
        if (onCreateStatement) onCreateStatement({ clinicId, from: outFrom, to: outTo });
    };

    // (2026-07-24) [CAD로 보내기] 제거 — 수집 건은 이미 CAD 작업이 끝난 파일. 주문목록 수신 의뢰서로 이동.

    // [외주 보내기] (#38, 2026-08-18) — 수집 건을 외주 의뢰서 전용 페이지에 프리필로 넘긴다 (기획서 3-3절 B).
    // 수집 케이스에는 납기가 없어 납기만 수동 입력. 치과명은 문서·프리필에 넣지 않는다(발주랩 영업정보, 기획서 3-6절).
    const sendToOutsource = (r) => {
        const indication = (r.indications || [])[0] || '';
        const teethDetails = (r.teethDetails && r.teethDetails.length)
            ? r.teethDetails.map(d => ({
                tooth: d.tooth, prosthesis: d.prosthesis || indication, material: d.material || r.material || '',
                shade: d.shade || '', implant: d.implant || '', ...(d.implantSystem ? { implantSystem: d.implantSystem } : {}), isPontic: !!d.isPontic, isBridge: !!d.isBridge,
            }))
            : (r.teeth || []).map(t => ({
                tooth: t, prosthesis: indication, material: r.material || '',
                shade: '', implant: '', isPontic: false, isBridge: false,
            }));
        navigate('/outsource-request', {
            state: {
                compose: true,
                prefill: {
                    patientName: displayPatientName(r).name || '',
                    teethDetails,
                    dueDate: '',   // 수집 케이스는 납기 미저장 — 수동 입력 (기획서 2절 미확인 사항 확정)
                    sourceRef: { sourceType: 'parsedCase', parsedCaseId: r.id },
                    sourceLabel: '수집함 케이스',
                },
            },
        });
    };

    const excludeOne = async (id) => {
        if (!window.confirm('이 수집 건을 명세서 대상에서 제외할까요? (제외 목록에서 복원 가능)')) return;
        try {
            await updateDoc(doc(db, 'parsedCases', id), { status: 'excluded', excludedAt: new Date() });
            // 성공 시에만 상태 반영 — 행은 [제외됨] 탭으로 이동 (2026-08-26 빈 catch 제거)
            setRows(prev => prev.map(r => r.id === id ? { ...r, status: 'excluded' } : r));
        } catch (e) {
            console.error('제외 처리 실패:', id, e?.code || e?.message);
            alert(e?.code === 'permission-denied'
                ? '제외는 기공소 대표 계정에서만 할 수 있습니다.'
                : '제외 처리에 실패했습니다. 잠시 후 다시 시도해주세요.');
        }
    };

    // 제외된 건 복원 — 다시 대기(pending) 상태로 되돌림
    const restoreOne = async (id) => {
        try {
            await updateDoc(doc(db, 'parsedCases', id), { status: 'pending', excludedAt: deleteField() });
            setRows(prev => prev.map(r => r.id === id ? { ...r, status: 'pending' } : r));
        } catch (e) {
            console.error('복원 실패:', e);
            alert('복원에 실패했습니다.');
        }
    };

    // ── 에이전트 연결 상태 배지 ──
    const toMs = (t) => {
        if (!t) return 0;
        if (typeof t.toDate === 'function') return t.toDate().getTime();
        const d = new Date(t); return isNaN(d) ? 0 : d.getTime();
    };
    const agoText = (ms) => {
        const min = Math.floor((Date.now() - ms) / 60000);
        if (min < 1) return '방금 전';
        if (min < 60) return `${min}분 전`;
        const h = Math.floor(min / 60);
        if (h < 24) return `${h}시간 전`;
        return `${Math.floor(h / 24)}일 전`;
    };
    const agentBadge = useMemo(() => {
        if (agentStatus === undefined) return null;   // 로딩 중엔 미표시
        const devices = agentStatus ? Object.values(agentStatus.devices || {}) : [];
        // 구버전 호환: devices 맵이 없고 최상위 lastSeen만 있는 문서
        if (!devices.length && agentStatus?.lastSeen) devices.push(agentStatus);
        if (!devices.length) {
            return {
                color: '#64748B', bg: '#F1F5F9', border: '#E2E8F0',
                text: '⚪ 에이전트 연결 이력 없음 — 덴클 에이전트를 설치하고 기공소 계정으로 로그인하면 CAD 파일이 자동 수집됩니다.',
                sub: null,
            };
        }
        const latest = devices.reduce((a, b) => (toMs(b.lastSeen) > toMs(a.lastSeen) ? b : a));
        const ms = toMs(latest.lastSeen);
        const staleMin = (Date.now() - ms) / 60000;
        const sub = null;   // [CAD로 보내기] 제거(2026-10-25) — CAD 스위치 안내 삭제
        if (staleMin <= 10) {
            return {
                color: '#15803D', bg: '#EDFBF1', border: '#BBF7D0',
                text: `🟢 에이전트 연결됨 · ${agoText(ms)}${latest.deviceName ? ` (${latest.deviceName}${latest.version ? `, v${latest.version}` : ''})` : ''}`,
                sub,
            };
        }
        return {
            color: '#B45309', bg: '#FFFBEB', border: '#FDE68A',
            text: `🟡 마지막 연결 ${agoText(ms)}${latest.deviceName ? ` (${latest.deviceName}${latest.version ? `, v${latest.version}` : ''})` : ''} — 에이전트가 실행 중인지 확인하세요.`,
            sub,
        };
    }, [agentStatus]);

    const th = { padding: '9px 10px', textAlign: 'left', fontSize: 12, color: '#64748B', fontWeight: 700, whiteSpace: 'nowrap' };
    const td = { padding: '8px 10px', fontSize: 13, color: '#334155' };

    return (
        <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
            <button onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#6366F1', cursor: 'pointer', fontSize: 14, marginBottom: 12 }}>
                <ChevronLeft size={16} /> 거래명세서로
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0F172A', margin: 0 }}>수집함 전체 목록</h2>
                <button onClick={load} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: 'none', borderRadius: 8, background: '#6366F1', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
                    <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} /> 새로고침
                </button>
            </div>
            {agentBadge && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                    <div style={{ alignSelf: 'flex-start', padding: '6px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 700, color: agentBadge.color, background: agentBadge.bg, border: `1px solid ${agentBadge.border}` }}>
                        {agentBadge.text}
                    </div>
                    {agentBadge.sub && (
                        <div style={{ alignSelf: 'flex-start', padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                            {agentBadge.sub}
                        </div>
                    )}
                </div>
            )}
            <p style={{ color: '#64748B', fontSize: 13, margin: '0 0 16px' }}>
                에이전트가 CAD 파일에서 자동 수집한 작업 내역입니다. 확인·제외용 화면이며, 명세서는 <b>[거래명세서] 메뉴</b>에서 치과와 기간을 골라 만듭니다.
            </p>

            {/* 필터 */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14, padding: 12, background: '#F8FAFC', borderRadius: 10 }}>
                {/* 상태 토글: 대기 / 명세서화됨 / 제외됨 / 전체 */}
                <div style={{ display: 'inline-flex', border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden' }}>
                    {[['all', '전체'], ['pending', '대기'], ['imported', '명세서에 담김'], ['excluded', '제외됨']].map(([v, lb]) => (
                        <button key={v} onClick={() => setStatusFilter(v)} style={{ padding: '7px 12px', fontSize: 12.5, fontWeight: 700, border: 'none', cursor: 'pointer', background: statusFilter === v ? '#6366F1' : '#fff', color: statusFilter === v ? '#fff' : '#64748B' }}>{lb}</button>
                    ))}
                </div>
                <select value={clinicFilter} onChange={e => setClinicFilter(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 13 }}>
                    {clinicOptions.map(c => <option key={c} value={c}>{c === 'all' ? '전체 치과' : c}</option>)}
                </select>
                <span style={{ color: '#94A3B8', fontSize: 13, marginLeft: 4 }}>작업일</span>
                <input type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 13 }} />
                <span style={{ color: '#94A3B8' }}>~</span>
                <input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 13 }} />
                {(from || to || clinicFilter !== 'all') && (
                    <button onClick={() => { setFrom(''); setTo(''); setClinicFilter('all'); }} style={{ padding: '6px 10px', fontSize: 12, border: '1px solid #E2E8F0', background: '#fff', borderRadius: 8, cursor: 'pointer', color: '#64748B' }}>필터 초기화</button>
                )}
                <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#64748B' }}>치과 줄을 클릭하면 환자 목록이 펼쳐집니다</span>
            </div>

            {loading ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>불러오는 중…</div>
            ) : visible.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8', background: '#F8FAFC', borderRadius: 10 }}>
                    {rows.length === 0 ? '수집된 대기 항목이 없습니다. 에이전트가 폴더를 감시 중인지 확인하세요.' : '필터에 해당하는 항목이 없습니다.'}
                </div>
            ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                            <th style={th}></th>
                            <th style={th}>치과</th>
                            <th style={th}>작업일</th>
                            <th style={th}>환자</th>
                            <th style={th}>치식</th>
                            <th style={th}>종류/재료</th>
                            <th style={th}>출처</th>
                            <th style={th}></th>
                        </tr></thead>
                        <tbody>
                            {grouped.map(([clinic, items]) => (
                                <React.Fragment key={clinic}>
                                    <tr style={{ background: '#EEF2FF', cursor: 'pointer' }} onClick={() => toggleClinic(clinic)}>
                                        <td style={{ ...td, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                            {/* 선택 체크박스 숨김 (2026-08-26) — [명세서 만들기]와 함께 숨김 상태. 되살리려면 주석 해제
                                            <input type="checkbox"
                                                checked={items.every(r => checked[r.id])}
                                                onChange={e => setChecked(prev => { const n = { ...prev }; items.forEach(r => n[r.id] = e.target.checked); return n; })} /> */}
                                        </td>
                                        <td style={{ ...td, fontWeight: 800, color: '#3730A3' }} colSpan={6}>
                                            <span style={{ display: 'inline-block', width: 16, color: '#6366F1' }}>{openClinics[clinic] ? '▾' : '▸'}</span>
                                            {clinic} <span style={{ fontWeight: 600, color: '#6366F1', fontSize: 12 }}>· {items.length}건{items[0]?.partnerId ? '' : ' (미연결)'}</span>
                                            <span style={{ fontWeight: 500, color: '#94A3B8', fontSize: 11, marginLeft: 8 }}>{openClinics[clinic] ? '접기' : '펼치기'}</span>
                                        </td>
                                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                                            {clinic !== '미확인' && (
                                                <button onClick={() => goClosingDraft(clinic, items)} title="거래명세서 화면에서 이 치과의 마감 초안을 만듭니다 (발행은 직접)"
                                                    style={{ background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer', color: '#4F46E5', fontSize: 12, fontWeight: 700, textDecoration: 'underline' }}>
                                                    {'이 치과 초안 만들기'}
                                                </button>
                                            )}
                                            {/* [명세서 만들기] 숨김 (2026-07-26 사장 지시) — 이 화면은 수집 자료 확인용.
                                                명세서 작성은 거래명세서 메뉴에서 치과·기간 선택으로 진행 (되살리려면 주석 해제) */}
                                        </td>
                                    </tr>
                                    {openClinics[clinic] && items.map(r => {
                                        const isImported = (r.status || 'pending') === 'imported';
                                        const isExcluded = r.status === 'excluded';
                                        return (
                                        <tr key={r.id} style={{ borderTop: '1px solid #F1F5F9', ...((isImported || isExcluded) ? { background: '#F8FAFC' } : {}) }}>
                                            <td style={{ ...td, textAlign: 'center' }}>
                                                {/* 선택 체크박스 숨김 (2026-08-26) — [명세서 만들기]와 함께 숨김 상태. 되살리려면 주석 해제
                                                {!isImported && !isExcluded && <input type="checkbox" checked={!!checked[r.id]} onChange={e => setChecked(prev => ({ ...prev, [r.id]: e.target.checked }))} />} */}
                                            </td>
                                            <td style={{ ...td, color: '#94A3B8' }}></td>
                                            <td style={{ ...td, whiteSpace: 'nowrap', color: '#64748B' }}>{(r.fileMtime || '').slice(0, 10)}</td>
                                            <td style={{ ...td, fontWeight: 600 }}>
                                                {(() => {
                                                    const p = displayPatientName(r);
                                                    return <>
                                                        {p.name || '환자미상'}
                                                        {p.raw && p.raw !== p.name && <span title={`파일 표기: ${p.raw}`} style={{ marginLeft: 5, fontSize: 11, fontWeight: 500, color: '#94A3B8' }}>{p.raw}</span>}
                                                    </>;
                                                })()}
                                                {r.isRemake && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, background: '#FEF2F2', color: '#DC2626', padding: '2px 8px', borderRadius: 6 }}>리메이크</span>}
                                                {isImported && (() => {
                                                    const b = inStatementBadge(r);
                                                    return <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, background: b.bg, color: b.c, padding: '2px 8px', borderRadius: 6 }}>{b.t}</span>;
                                                })()}
                                                {isExcluded && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, background: '#F1F5F9', color: '#64748B', padding: '2px 8px', borderRadius: 6 }}>제외됨</span>}
                                            </td>
                                            <td style={td}>{(r.teeth || []).join(', ') || '-'}</td>
                                            <td style={{ ...td, color: '#475569' }}>{[(r.indications || [])[0], r.material].filter(Boolean).join(' / ') || '-'}</td>
                                            <td style={td}><span style={{ fontSize: 11, fontWeight: 700, background: '#EEF2FF', color: '#6366F1', padding: '2px 8px', borderRadius: 6 }}>{r.sourceType}</span></td>
                                            <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {isExcluded ? (
                                                    <button onClick={() => restoreOne(r.id)} title="제외 해제 — 대기 목록으로 복원" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 9px', border: '1px solid #C7D2FE', background: '#EEF2FF', borderRadius: 6, cursor: 'pointer', color: '#4338CA', fontSize: 12, fontWeight: 700 }}>
                                                        <RotateCcw size={12} /> 복원
                                                    </button>
                                                ) : isImported ? (
                                                    <button onClick={() => revertToInbox(r)} title="수집함으로 되돌리기 (의뢰서 삭제)" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 9px', border: '1px solid #E2E8F0', background: '#fff', borderRadius: 6, cursor: 'pointer', color: '#64748B', fontSize: 12, fontWeight: 700 }}>
                                                        <RotateCcw size={12} /> 되돌리기
                                                    </button>
                                                ) : (
                                                    <>
                                                        {/* [외주 보내기] (#38) — 대표 지시로 숨김(2026-08-18 "일단 없애줘": 에이전트 중심 정리(P1) 전까지).
                                                            복원 = 아래 주석 해제 (sendToOutsource 핸들러는 유지)
                                                        <button onClick={() => sendToOutsource(r)} title="이 수집 건 내용으로 외주 의뢰서가 자동 작성됩니다 (기공소 → 기공소 CAD 디자인 의뢰)"
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 9px', marginRight: 6, border: '1px solid #C7D2FE', background: '#EEF2FF', borderRadius: 6, cursor: 'pointer', color: '#4338CA', fontSize: 12, fontWeight: 700 }}>
                                                            외주 보내기
                                                        </button> */}
                                                        {!isStaff && <button onClick={() => excludeOne(r.id)} title="제외" style={{ display: 'inline-flex', padding: 5, border: '1px solid #FECACA', background: '#FEF2F2', borderRadius: 6, cursor: 'pointer', color: '#DC2626' }}>
                                                            <Trash2 size={13} />
                                                        </button>}
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default ParsedCasesInbox;
